import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { Pencil, Trash2 } from 'lucide-react';
import ApiService from '../../../../../services/ApiService';
import {
  StandardDataTable,
  useColumnsState,
  StandardColumn,
} from '../../../../../components/StandardPage';

const taxScenarioScreenKey = 'tax_scenario';

const ScenarioTable: React.FC = () => {
  const navigate = useNavigate();
  const { refresh, scenarioFilters } = useOutletContext<{ refresh?: boolean; scenarioFilters?: any[] }>() ?? {};
  const [scenarios, setScenarios] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const handleDelete = async (id: number, nome: string) => {
    if (!window.confirm(`Excluir o cenário "${nome}"? Esta ação não pode ser desfeita.`)) return;
    try {
      await ApiService.fiscal.deleteTaxConfig(id);
      setScenarios(prev => prev.filter(s => s.id !== id));
    } catch (error: any) {
      alert(error?.response?.data?.message || 'Erro ao excluir cenário.');
    }
  };

  const fetchScenarios = useCallback(async () => {
    setIsLoading(true);
    try {
      const params: { filters?: string } = {};
      if (scenarioFilters && scenarioFilters.length > 0) {
        params.filters = JSON.stringify(scenarioFilters);
      }
      const response = await ApiService.fiscal.getTaxConfigs(params);
      const data = Array.isArray(response.data) ? response.data : response.data.data;
      setScenarios(data || []);
    } catch (error) {
      console.error('Erro ao buscar cenários:', error);
    } finally {
      setIsLoading(false);
    }
  }, [scenarioFilters]);

  useEffect(() => { fetchScenarios(); }, [fetchScenarios, refresh]);

  const columns = useMemo<StandardColumn<any>[]>(() => [
    {
      id: 'nome',
      label: 'Nome',
      accessor: (row) => row.nome || 'Sem nome',
      cell: (row) => <strong>{row.nome || 'Sem nome'}</strong>,
    },
    {
      id: 'cnpjOrigem',
      label: 'CNPJ de origem',
      sortable: false,
      accessor: (row) => `${row.CompanyEmissor?.cnpj || '—'} · ${row.CompanyEmissor?.state?.name || ''}`,
      cell: (row) => (
        <span style={{ color: '#999', fontSize: '13px' }}>
          {`${row.CompanyEmissor?.cnpj || '—'} · ${row.CompanyEmissor?.state?.name || ''}`}
        </span>
      ),
    },
    {
      id: 'cfop',
      label: 'CFOP',
      accessor: (row) => row.Cfop?.code ?? '',
      cell: (row) => <strong style={{ color: '#0085CC' }}>{row.Cfop?.code}</strong>,
    },
    {
      id: 'aliquotaIPI',
      label: 'IPI%',
      accessor: (row) => `${row.aliquotaIPI || '0.00'}%`,
    },
    {
      id: 'cenarios',
      label: 'Cenários',
      sortable: false,
      accessor: (row) => row.TaxScenario?.length || 0,
      cell: (row) => <strong>{row.TaxScenario?.length || 0}</strong>,
    },
  ], []);

  const { columnsState, handleColumnsChange, handleRestoreColumns } =
    useColumnsState(taxScenarioScreenKey, columns);

  if (isLoading) {
    return (
      <p style={{ padding: '40px', textAlign: 'center', color: '#8c98a4' }}>Carregando cenários...</p>
    );
  }

  return (
    <StandardDataTable
      columns={columns}
      data={scenarios}
      columnsState={columnsState}
      onColumnsChange={handleColumnsChange}
      onRestoreColumns={handleRestoreColumns}
      countLabel='cenários'
      exportFileName='cenarios-fiscais'
      emptyMessage='Nenhum cenário encontrado.'
      rowActions={(row) => [
        { label: 'Editar', icon: <Pencil size={16} />, onClick: () => navigate(`/panel/invoice/tax-scenario/create?editId=${row.id}`) },
        { label: 'Excluir', icon: <Trash2 size={16} />, onClick: () => handleDelete(row.id, row.nome || 'sem nome') },
      ]}
    />
  );
};

export default ScenarioTable;
